import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AttentionCard, MetricCard, DecisionCard, CompactRow, StatusBadge,
  LoadingState, EmptyState, ErrorState } from './osPrimitives';

const API = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);

async function getJSON(path) {
  const token = localStorage.getItem('ace_admin_token');
  const r = await fetch(`${API}${path}`, {
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!r.ok) throw new Error(`${path} ${r.status}`);
  return r.json();
}

const LEVEL_STATUS = { danger: 'risk', warning: 'attention', info: 'waiting' };

// Live MY ACE — composes existing canonical endpoints only. No new backend, no new task engine.
export default function MyAceLive() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true });

  const load = async () => {
    setState({ loading: true });
    const out = {};
    const safe = async (k, p) => { try { out[k] = await getJSON(p); } catch (e) { out[k + 'Err'] = true; } };
    await Promise.all([
      safe('alerts', '/api/command-center/alerts'),
      safe('bookings', '/api/command-center/confirmed-bookings'),
      safe('tasks', '/api/marketing/tasks?status=pending'),
      safe('pulse', '/api/command-center/ops-dashboard'),
      safe('board', '/api/fulfillment/control/board'),
    ]);
    setState({ loading: false, ...out });
  };
  useEffect(() => { load(); }, []);

  if (state.loading) return <LoadingState rows={4} />;

  const td = today();
  const alerts = (state.alerts?.alerts) || [];
  const bookings = (state.bookings?.bookings) || [];
  const todayBk = bookings.filter((b) => (b.event_date || '').slice(0, 10) === td);
  const upcomingBk = bookings.filter((b) => (b.event_date || '') > td).slice(0, 6);
  const tasks = (state.tasks?.tasks) || [];
  const overdue = tasks.filter((t) => t.due_date && t.due_date <= td);
  const upcomingTasks = tasks.filter((t) => !t.due_date || t.due_date > td);
  const pulse = state.pulse?.kpis || state.pulse || {};
  const board = state.board;
  const decisions = board ? [
    ...(board.reconciliation || []).filter((r) => r.status === 'READY').slice(0, 3).map((r) => ({
      question: `Approve payout · ${r.provider_name || r.provider_id}`, note: `${r.reconciliation_id} · $${r.gross_payable}`,
      options: [{ label: 'Open in Network → Payouts', recommended: true }], to: `/os/network?payouts=ready&rec=${r.reconciliation_id}` })),
    ...(board.disputes || []).slice(0, 2).map((d) => ({
      question: `Resolve dispute · ${d.provider_name || d.provider_id}`, note: d.reason,
      options: [{ label: 'Open in Network → Payouts' }], to: `/os/network?payouts=disputed&rec=${d.reconciliation_id || ''}` })),
  ] : [];

  return (
    <div className="os-animate space-y-6" data-testid="my-ace-live">
      {/* Needs Attention */}
      <Block title="Needs Attention" testid="mya-attention">
        {state.alertsErr ? <ErrorState body="Alerts unavailable." onRetry={load} />
          : alerts.length === 0 ? <EmptyState title="Nothing needs attention right now" body="This is a positive operational state." />
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {alerts.slice(0, 8).map((a, i) => (
                <AttentionCard key={i} title={a.title} reason={a.detail} status={LEVEL_STATUS[a.level] || 'attention'}
                  actionLabel={a.link ? 'Open' : 'Review'} onAction={a.link ? () => navigate(a.link) : undefined} testid={`mya-alert-${i}`} />
              ))}
            </div>}
      </Block>

      {/* Business Pulse */}
      <Block title="Business Pulse" testid="mya-pulse">
        {state.pulseErr ? <ErrorState body="Pulse unavailable." onRetry={load} />
          : <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <MetricCard label="Pickups Today" value={pulse.todays_pickups ?? undefined} unknown={pulse.todays_pickups === undefined} />
              <MetricCard label="Vehicles Out" value={pulse.vehicles_out ?? undefined} unknown={pulse.vehicles_out === undefined} />
              <MetricCard label="Fulfillment Ready" value={(board?.counts?.reconciliation) ?? undefined} unknown={!board} />
              <MetricCard label="Open Disputes" value={(board?.counts?.disputes) ?? undefined} unknown={!board} />
            </div>}
      </Block>

      {/* Today */}
      <Block title="Today" testid="mya-today">
        {state.bookingsErr ? <ErrorState body="Bookings unavailable." onRetry={load} />
          : (todayBk.length + upcomingBk.length) === 0 ? <EmptyState title="No services scheduled" body="No confirmed bookings for today or upcoming." />
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div><p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--os-muted)' }}>Services Today ({todayBk.length})</p>
                {todayBk.length ? todayBk.map((b, i) => <CompactRow key={`${b.booking_id || 'bk'}-${i}`} title={b.client_name || 'Client'} subtitle={`${b.vehicle || 'Vehicle'} · ${b.pickup_time || b.event_date}`} status={b.cc_status === 'completed' ? 'ready' : 'waiting'} right={`$${b.total || 0}`} onOpen={() => navigate(b.booking_id ? `/os/services/${b.booking_id}` : '/os/services?tab=booked')} />) : <p className="text-xs" style={{ color: 'var(--os-muted)' }}>—</p>}
              </div>
              <div><p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--os-muted)' }}>Upcoming ({upcomingBk.length})</p>
                {upcomingBk.length ? upcomingBk.map((b, i) => <CompactRow key={`${b.booking_id || 'bk'}-${i}`} title={b.client_name || 'Client'} subtitle={`${b.vehicle || 'Vehicle'} · ${b.event_date}`} status="waiting" right={`$${b.total || 0}`} onOpen={() => navigate(b.booking_id ? `/os/services/${b.booking_id}` : '/os/services?tab=booked')} />) : <p className="text-xs" style={{ color: 'var(--os-muted)' }}>—</p>}
              </div>
            </div>}
      </Block>

      {/* My Work */}
      <Block title="My Work" testid="mya-work">
        {state.tasksErr ? <ErrorState body="Tasks unavailable." onRetry={load} />
          : tasks.length === 0 ? <EmptyState title="No open follow-ups" body="You're all caught up." />
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div><p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--os-danger)' }}>Due / Overdue ({overdue.length})</p>
                {overdue.length ? overdue.slice(0, 6).map((t) => <CompactRow key={t.task_id} title={t.title} subtitle={t.contact_name || t.description} status="attention" right={t.due_date} onOpen={() => navigate('/command-center?tab=clients')} />) : <p className="text-xs" style={{ color: 'var(--os-muted)' }}>—</p>}
              </div>
              <div><p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: 'var(--os-muted)' }}>Upcoming ({upcomingTasks.length})</p>
                {upcomingTasks.length ? upcomingTasks.slice(0, 6).map((t) => <CompactRow key={t.task_id} title={t.title} subtitle={t.contact_name || t.description} status="waiting" right={t.due_date || '—'} onOpen={() => navigate('/command-center?tab=clients')} />) : <p className="text-xs" style={{ color: 'var(--os-muted)' }}>—</p>}
              </div>
            </div>}
      </Block>

      {/* Decisions */}
      <Block title="Decisions" testid="mya-decisions">
        {state.boardErr ? <ErrorState body="Fulfillment control unavailable." onRetry={load} />
          : decisions.length === 0 ? <EmptyState title="No approvals waiting" body="No reconciliation approvals or disputes need you." />
          : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {decisions.map((d, i) => <DecisionCard key={i} question={d.question} note={d.note} options={d.options} onChoose={() => navigate(d.to)} testid={`mya-decision-${i}`} />)}
            </div>}
      </Block>
    </div>
  );
}

const Block = ({ title, testid, children }) => (
  <section data-testid={testid}>
    <h2 className="os-display text-2xl mb-3">{title}</h2>
    {children}
  </section>
);
